import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowUpRight, Loader2, CalendarClock, Save, CheckCircle2 } from "lucide-react";

const BUDGETS = ["Not sure yet", "Under $1,000", "$1,000 – $3,000", "$3,000 – $7,000", "$7,000+"];
const TIMELINES = ["ASAP", "Within a month", "1 – 3 months", "Just exploring"];
const CONTACTS = ["Email", "Phone call", "Text message"];

const F = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-gray-400">{label}{hint && <span className="text-gray-600 normal-case tracking-normal"> · {hint}</span>}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
const inputCls = "w-full bg-white/5 border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none placeholder:text-gray-600";

export function CustomBuildModal({ open, onClose, template, source = "" }) {
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: "", email: "", business_name: "", phone: "",
    industry: "", budget: "", timeline: "", goals: "",
    preferred_contact: "", preferred_time: "",
  });
  const [busy, setBusy] = useState(null); // which action is submitting
  const [done, setDone] = useState(null); // "save" | "schedule"
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) {
      setDone(null);
      setF((p) => ({ ...p, industry: p.industry || template?.category || "" }));
    }
  }, [open, template]);

  if (!open) return null;

  const submit = async (action) => {
    if (!f.name.trim() || !f.email.trim()) {
      toast.error("Please add your name and email so we can reach you.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(action);
    try {
      await api.post("/inquiries", {
        ...f,
        template_slug: template?.slug || "",
        template_title: template?.title || "",
        action,
        cta_source: source,
      });
      if (action === "assessment") {
        onClose?.();
        const q = new URLSearchParams();
        if (template?.title) q.set("design", template.title);
        if (f.industry) q.set("industry", f.industry);
        navigate(`/start?${q.toString()}`);
        return;
      }
      setDone(action);
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="custom-build-modal">
      <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" onClick={() => onClose?.()} />
      <div className="relative w-full max-w-lg bg-[#0b1327] border border-white/12 shadow-2xl max-h-[92vh] overflow-y-auto">
        <button onClick={() => onClose?.()} data-testid="custom-build-close"
          className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"><X size={20} /></button>

        {done ? (
          <div className="p-8 text-center" data-testid="custom-build-success">
            <CheckCircle2 size={44} className="text-gold mx-auto" />
            <h3 className="font-heading text-2xl text-white mt-4">
              {done === "schedule" ? "Consultation requested" : "Project saved"}
            </h3>
            <p className="text-sm text-gray-300 mt-3 leading-relaxed">
              Thanks, {f.name.split(" ")[0]}. We've got your details{template?.title ? <> for a build inspired by <span className="text-gold">{template.title}</span></> : null}. Ask Top Foster will reach out{f.preferred_time ? ` around ${f.preferred_time}` : " shortly"} to map out your custom website.
            </p>
            <button onClick={() => onClose?.()} data-testid="custom-build-success-close"
              className="mt-6 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">Done</button>
            <button onClick={() => { onClose?.(); const q = new URLSearchParams(); if (template?.title) q.set("design", template.title); if (f.industry) q.set("industry", f.industry); navigate(`/start?${q.toString()}`); }}
              className="block w-full mt-3 text-sm text-gray-400 hover:text-gold">Or complete the full assessment now →</button>
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-gold">Custom build</div>
            <h3 className="font-heading text-2xl text-white mt-1.5 leading-snug">Let's build this for your business</h3>
            <p className="text-sm text-gray-400 mt-2">
              {template?.title ? <>Inspired by <span className="text-gray-200">{template.title}</span>. </> : null}
              Tell us where to reach you — takes 30 seconds.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <F label="Your name"><input data-testid="cb-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Foster" /></F>
              <F label="Email"><input data-testid="cb-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@business.com" /></F>
              <F label="Business name" hint="optional"><input data-testid="cb-business" className={inputCls} value={f.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Foster & Co." /></F>
              <F label="Phone" hint="optional"><input data-testid="cb-phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" /></F>
              <F label="Industry"><input data-testid="cb-industry" className={inputCls} value={f.industry} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Roofing" /></F>
              <F label="Preferred contact" hint="optional">
                <select data-testid="cb-contact" className={inputCls} value={f.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)}>
                  <option value="">No preference</option>
                  {CONTACTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </F>
              <F label="Budget" hint="optional">
                <select data-testid="cb-budget" className={inputCls} value={f.budget} onChange={(e) => set("budget", e.target.value)}>
                  <option value="">Select a range</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </F>
              <F label="Timeline" hint="optional">
                <select data-testid="cb-timeline" className={inputCls} value={f.timeline} onChange={(e) => set("timeline", e.target.value)}>
                  <option value="">Select</option>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </F>
            </div>
            <div className="mt-4">
              <F label="What do you want this website to do for you?" hint="optional">
                <textarea data-testid="cb-goals" rows={2} className={inputCls} value={f.goals} onChange={(e) => set("goals", e.target.value)} placeholder="e.g. Get more estimate requests from local homeowners" />
              </F>
            </div>
            <div className="mt-4">
              <F label="Best days / times to reach you" hint="for a consultation">
                <input data-testid="cb-time" className={inputCls} value={f.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} placeholder="e.g. Weekday mornings" />
              </F>
            </div>

            <div className="mt-7 space-y-3">
              <button onClick={() => submit("assessment")} disabled={!!busy} data-testid="cb-assessment"
                className="w-full bg-gold text-navy py-3.5 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {busy === "assessment" ? <Loader2 className="animate-spin" size={18} /> : <>Continue to full assessment <ArrowUpRight size={17} /></>}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => submit("schedule")} disabled={!!busy} data-testid="cb-schedule"
                  className="border border-white/20 text-white py-3 font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60">
                  {busy === "schedule" ? <Loader2 className="animate-spin" size={16} /> : <><CalendarClock size={16} /> Schedule a call</>}
                </button>
                <button onClick={() => submit("save")} disabled={!!busy} data-testid="cb-save"
                  className="border border-white/20 text-white py-3 font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60">
                  {busy === "save" ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Save my project</>}
                </button>
              </div>
              <p className="text-[11px] text-gray-500 text-center pt-1">No spam. Your details go straight to Ask Top Foster.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
